import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL } from './baseUrl';

const API_URL = API_BASE_URL;

class AuthService {
  private apiClient: AxiosInstance;

  constructor() {
    this.apiClient = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Set token from localStorage on initialization
    const token = localStorage.getItem('authToken');
    if (token) {
      this.setAuthToken(token);
    }
  }

  setAuthToken(token: string | null) {
    if (token) {
      this.apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('authToken', token);
    } else {
      delete this.apiClient.defaults.headers.common['Authorization'];
      localStorage.removeItem('authToken');
    }
  }

  getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  async register(email: string, password: string, firstName: string, lastName: string) {
    const response = await this.apiClient.post('/auth/register', {
      email,
      password,
      firstName,
      lastName
    });
    
    if (response.data.token) {
      this.setAuthToken(response.data.token);
    }
    
    return response.data;
  }

  async login(email: string, password: string) {
    const response = await this.apiClient.post('/auth/login', {
      email,
      password
    });
    
    if (response.data.token) {
      this.setAuthToken(response.data.token);
    }
    
    return response.data;
  }

  async getCurrentUser() {
    const response = await this.apiClient.get('/auth/me');
    return response.data.user;
  }

  async updateProfile(firstName: string, lastName: string) {
    const response = await this.apiClient.put('/auth/me', {
      firstName,
      lastName
    });
    return response.data.user || response.data.data;
  }

  async updateNotificationPreference(notifyOnAnalysisComplete: boolean) {
    const response = await this.apiClient.put('/auth/preferences', {
      notifyOnAnalysisComplete
    });
    return response.data.user;
  }

  async changePassword(currentPassword: string, newPassword: string) {
    const response = await this.apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  }

  async addCredits(amount: number) {
    const response = await this.apiClient.post('/auth/credits/add', {
      amount
    });
    return response.data.user;
  }

  async deleteAccount() {
    await this.apiClient.delete('/auth/me');
  }

  logout() {
    this.setAuthToken(null);
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }
}

export default new AuthService();
